// apps/admin/src/components/SystemEventListener.tsx
//
// Phase 8.3 Wave 5 Task 65 — global SA-only system.* event mount.
//
// Why this component exists:
//   - `useSystemChannel` must be mounted exactly once, near the top of
//     the React tree, so every page receives SA notifications without
//     each page wiring its own subscription. Wave-mate task-36 ships the
//     admin sidebar groups; this listener is a sibling of that mount.
//   - Per CONTEXT only Super Admin sees `system.*` events. The
//     listener itself renders nothing for non-SA — the hook short-
//     circuits internally — so it's safe to mount unconditionally above
//     the route tree.
//   - Per INTERFACE-STANDARDS the toast/banner copy comes from the WS
//     payload (`title` / `description`); HTTP/transport copy is never
//     surfaced. The hook honours `error.user_message || error.message`
//     priority via the toast variant mapping when payloads carry an
//     embedded API envelope (rare, but used by approval.expired which
//     forwards the originating 410 error body).
//
// Caller hand-off: mount inside the authenticated layout in App.tsx,
// e.g. `<AdminAppShellLayout><SystemEventListener />...</...>` so
// unauthenticated routes (/login etc.) never subscribe.
//
// vg-binding: CONTEXT:decisions=P8.D-212,P8.D-260
// vg-binding: INTERFACE-STANDARDS:error-shape

import type { JSX } from 'react';
import { useSystemChannel } from '../lib/ws-system-channel.js';

/**
 * SystemEventListener — render-less component. Mounts the
 * `useSystemChannel` hook so SA users receive `system.*` events
 * globally. Non-SA users get a no-op (hook short-circuits before
 * subscribing).
 *
 * Returns a hidden `<span aria-hidden>` carrying a stable `data-testid`
 * so smoke tests can assert the listener is mounted without inspecting
 * the WS internals. The component itself renders no visible UI — the
 * actual banner / bell / toast surfaces live elsewhere (task-03
 * NotificationBell + task-36 sidebar badge + Toaster at main.tsx root).
 */
export function SystemEventListener(): JSX.Element {
  const { enabled, lastEventId } = useSystemChannel();
  return (
    <span
      aria-hidden="true"
      data-testid="system-event-listener"
      data-system-events-enabled={enabled ? 'true' : 'false'}
      data-last-event-id={lastEventId ?? ''}
      className="sr-only"
    />
  );
}

export default SystemEventListener;
